import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useBrandManager } from "@/hooks/useBrandManager";
import { addDays, addWeeks, addMonths, format, endOfYear } from "date-fns";

interface Sprint {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  frequency: string;
  status: 'cancelled' | 'open' | 'closed';
  user_id: string;
  brand_id: string;
  created_at: string;
  updated_at: string;
}

interface CreateSprintData {
  frequency: 'weekly' | 'bi-weekly' | 'tri-weekly' | 'monthly';
  year: number;
  startingDate: Date;
}

interface UpdateSprintData {
  name?: string;
  start_date?: string;
  end_date?: string;
  status?: 'cancelled' | 'open' | 'closed';
}

export const useSprints = () => {
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();

  const fetchSprints = async () => {
    if (!user || !currentBrandId) {
      setSprints([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('growth_sprints')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('start_date', { ascending: true });

      if (error) throw error;
      
      // Type assertion to ensure proper typing
      const typedData = (data || []).map(sprint => ({
        ...sprint,
        status: sprint.status as 'cancelled' | 'open' | 'closed'
      }));
      
      setSprints(typedData);
    } catch (error: any) {
      console.error('Error fetching sprints:', error);
      toast({
        title: "Error",
        description: "Failed to fetch sprints",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateSprintName = (startDate: Date, sprintNumber: number): string => {
    const monthAbbr = format(startDate, 'MMM');
    const day = format(startDate, 'd');
    return `${monthAbbr} ${day}, Sprint ${sprintNumber}`;
  };

  const calculateSprintDuration = (frequency: string): number => {
    switch (frequency) {
      case 'weekly': return 7;
      case 'bi-weekly': return 14;
      case 'tri-weekly': return 21;
      case 'monthly': return 30;
      default: return 14;
    }
  };

  const createSprint = async (data: CreateSprintData): Promise<boolean> => {
    if (!user || !currentBrandId) {
      toast({
        title: "Error",
        description: "Please log in and select a brand first",
        variant: "destructive",
      });
      return false;
    }

    try {
      setIsLoading(true);

      const sprintDuration = calculateSprintDuration(data.frequency);
      const yearEnd = endOfYear(new Date(data.year, 0, 1));
      const sprintsToCreate: any[] = [];
      
      let currentStartDate = new Date(data.startingDate);
      let sprintNumber = 1;

      while (currentStartDate <= yearEnd) {
        const endDate = addDays(currentStartDate, sprintDuration - 1);
        
        if (endDate > yearEnd) break;

        const sprintName = generateSprintName(currentStartDate, sprintNumber);
        
        sprintsToCreate.push({
          name: sprintName,
          start_date: format(currentStartDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
          frequency: data.frequency,
          status: 'open',
          user_id: user.id,
          brand_id: currentBrandId
        });

        switch (data.frequency) {
          case 'weekly':
            currentStartDate = addWeeks(currentStartDate, 1);
            break;
          case 'bi-weekly':
            currentStartDate = addWeeks(currentStartDate, 2);
            break;
          case 'tri-weekly':
            currentStartDate = addWeeks(currentStartDate, 3);
            break;
          case 'monthly':
            currentStartDate = addMonths(currentStartDate, 1);
            break;
        }

        sprintNumber++;
      }

      const { error } = await supabase
        .from('growth_sprints')
        .insert(sprintsToCreate);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Created ${sprintsToCreate.length} sprints for ${data.year}`,
      });

      await fetchSprints();
      return true;
    } catch (error: any) {
      console.error('Error creating sprints:', error);
      toast({
        title: "Error",
        description: "Failed to create sprints",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateSprint = async (sprintId: string, updateData: UpdateSprintData): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('growth_sprints')
        .update(updateData)
        .eq('id', sprintId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Sprint updated successfully",
      });

      await fetchSprints();
      return true;
    } catch (error: any) {
      console.error('Error updating sprint:', error);
      toast({
        title: "Error",
        description: "Failed to update sprint",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSprint = async (sprintId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('growth_sprints')
        .delete()
        .eq('id', sprintId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Sprint deleted successfully",
      });

      await fetchSprints();
      return true;
    } catch (error: any) {
      console.error('Error deleting sprint:', error);
      
      // Check if it's a foreign key constraint error (tasks assigned to sprint)
      const errorMessage = error?.message || error?.code || '';
      const isConstraintError = errorMessage.includes('foreign key') || 
                                errorMessage.includes('fk_growth_tasks_sprint') ||
                                error?.code === '23503';
      
      toast({
        title: "Error",
        description: isConstraintError 
          ? "Cannot delete sprint: Tasks are assigned to this sprint. Please reassign or delete the tasks first."
          : "Failed to delete sprint. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSprints();
  }, [user, currentBrandId]);

  return {
    sprints,
    isLoading,
    createSprint,
    updateSprint,
    deleteSprint,
    refetch: fetchSprints,
  };
};
